import React, { useState, useEffect } from "react";
import { Search, UserPlus, RotateCcw } from "lucide-react"; 
import { useNavigate } from "react-router-dom";
import AddCustomerForm from "./AddCustomerForm"; 
import PolicyActionsModal from "./PolicyActionsModal"

// --- Data Structure Matching the Image ---
const initialCustomers = [
    { name: "Jean Baptiste Mballa", id: "CM-12345678", policy: "SW-2024-001234", vehicle: "Toyota Camry", reg: "CM-YDE-1234-AB", phone: "+237 677 123 456", status: "Active", premium: "620 250 FCFA", expiry: "15/10/2024", expired: true },
    { name: "Paul Nguema", id: "CM-87654321", policy: "SW-2024-002345", vehicle: "Mercedes Sprinter", reg: "CM-DLA-5678-CD", phone: "+237 699 987 654", status: "Active", premium: "1 250 000 FCFA", expiry: "25/09/2024", expired: true },
    { name: "Fatima Hassan", id: "CM-11223344", policy: "SW-2024-003456", vehicle: "Honda CBR 250", reg: "CM-YDE-0987-EF", phone: "+237 655 111 222", status: "Active", premium: "270 000 FCFA", expiry: "15/09/2024", expired: true },
    { name: "short", id: "12345678", policy: "1234567", vehicle: "", reg: "", phone: "", status: "Active", premium: "0 FCFA", expiry: "14/01/2027", expired: false },
];

const CustomerList = () => {
    const navigate = useNavigate();

    // Load/Save from localStorage
    const [customers, setCustomers] = useState(() => {
        const saved = localStorage.getItem("customers_list");
        return saved ? JSON.parse(saved) : initialCustomers;
    });

    const [search, setSearch] = useState("");
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
    const [selectedCustomerForActions, setSelectedCustomerForActions] = useState(null);
    const [message, setMessage] = useState("");

    useEffect(() => {
        localStorage.setItem("customers_list", JSON.stringify(customers));
    }, [customers]);

    const showMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(""), 3000);
    };

    const handleView = (customerId) => {
        navigate(`/customers/view/${customerId}`);
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setShowForm(true);
    };

    const handleCloseModal = () => {
        setShowForm(false);
        setEditingCustomer(null);
    };

    const handleOpenActionsModal = (customer) => {
        setSelectedCustomerForActions(customer);
        setIsActionsModalOpen(true);
    };

    const handlePolicyAction = (customerId, data, successMessage) => {
        const { Action, ...updateFields } = data;

        setCustomers(prevCustomers => {
            if (Action === "Archive") {
                const updatedCustomers = prevCustomers.map(c => {
                    if (c.id === customerId) {
                        return { ...c, status: "Terminated", expired: true };
                    }
                    return c;
                });
                showMessage(successMessage);
                return updatedCustomers;
            }

            return prevCustomers.map(c => {
                if (c.id === customerId) {
                    const updatedCustomer = {
                        ...c,
                        ...updateFields
                    };

                    if (updatedCustomer.expiry && updatedCustomer.expiry !== "N/A") {
                        const parts = updatedCustomer.expiry.split("/");
                        const expiryDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        updatedCustomer.expired = expiryDate < new Date();
                    } else if (updatedCustomer.status !== "Active") {
                        updatedCustomer.expired = true;
                    }

                    return updatedCustomer;
                }
                return c;
            });
        });

        showMessage(successMessage);
        setIsActionsModalOpen(false);
        setSelectedCustomerForActions(null);
    };

    const handleFormSubmit = (formData) => {
        const isEditing = !!editingCustomer;

        let newCustomerObject = {
            ...formData,
            name: formData["Driver Name*"] || "",
            policy: formData["Policy Number"] || "N/A",
            phone: formData["Telephone Number"] || "N/A",
            vehicle: formData["Make*"] || "N/A",
            reg: formData["Registration Number"] || "N/A",
            status: isEditing ? (formData["Status"] || "Active") : "Active",
            premium: formData["Net Premium (XAF)"] || "0 FCFA",
            expiry: formData["Expiration Date"] ? formData["Expiration Date"].split("-").reverse().join("/") : "N/A",
            expired: formData["Expiration Date"] ? new Date(formData["Expiration Date"]) < new Date() : false,
        };

        if (isEditing) {
            const updatedCustomers = customers.map(c =>
                c.id === editingCustomer.id
                    ? { ...c, ...newCustomerObject, id: editingCustomer.id }
                    : c
            );
            setCustomers(updatedCustomers);
        } else {
            newCustomerObject.id = "CM-" + Math.floor(Math.random() * 10000000);
            setCustomers(prev => [...prev, newCustomerObject]);
        }

        handleCloseModal();
    };

    const handleDelete = (customerId, customerName) => {
        if (window.confirm(`Are you sure you want to delete ${customerName}? It will be moved to the Archive and can be restored later.`)) {
            setCustomers(prevCustomers =>
                prevCustomers.map(c =>
                    c.id === customerId ? { ...c, status: "Deleted", expired: true } : c
                )
            );
            showMessage(`Customer ${customerName} moved to Archive (Deleted).`);
        }
    };

    const activeCustomers = customers.filter(c => c.status === "Active");

    const filteredCustomers = activeCustomers.filter(c =>
        Object.values(c).some(val =>
            String(val).toLowerCase().includes(search.toLowerCase())
        )
    );

    const expiringCustomers = customers.filter(c => c.expired).length;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">

            {message && (
                <div className="fixed top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl z-50">
                    {message}
                </div>
            )}

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-semibold text-gray-800">Active Customer Policies</h1>

                <button
                    onClick={() => { setShowForm(true); setEditingCustomer(null); }}
                    className="flex items-center space-x-2 bg-gray-900 text-white px-4 py-2 rounded shadow hover:bg-gray-700"
                >
                    <UserPlus size={16} />
                    <span>Add New Customer</span>
                </button>
            </div>

            <div className="bg-red-100 border border-red-500 text-red-700 p-3 mb-4 rounded-md">
                <p className="font-medium text-sm">
                    ⚠️ <strong>{expiringCustomers}</strong> customers have policies expiring soon or already expired!
                </p>
            </div>

            <div className="relative mb-6">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                    type="search"
                    placeholder="Search by name, policy number, registration, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900"
                />
            </div>

            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {[
                                "Customer Name",
                                "Policy Number",
                                "Vehicle",
                                "Phone",
                                "Status",
                                "Premium",
                                "Expiry Date",
                                "Actions",
                            ].map(header => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCustomers.map((customer, index) => (
                            <tr key={index} className={customer.expired ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"}>

                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className={customer.expired ? "text-red-600 font-medium" : "text-gray-900 font-medium"}>
                                        {customer.name}
                                    </div>
                                    <div className="text-xs text-gray-500">{customer.id}</div>
                                </td>

                                <td className={`px-4 py-3 ${customer.expired ? "text-red-600" : "text-gray-500"}`}>
                                    {customer.policy}
                                </td>

                                <td className="px-4 py-3">
                                    <div className="text-gray-900">{customer.vehicle}</div>
                                    <div className="text-xs text-gray-500">{customer.reg}</div>
                                </td>

                                <td className="px-4 py-3 text-gray-500">{customer.phone}</td>

                                <td className="px-4 py-3">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        {customer.status}
                                    </span>
                                </td>

                                <td className="px-4 py-3 text-gray-700">{customer.premium}</td>

                                <td className={`px-4 py-3 font-bold ${customer.expired ? "text-red-600" : "text-gray-900"}`}>
                                    {customer.expiry}
                                    {customer.expired && (
                                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">
                                            Expired
                                        </span>
                                    )}
                                </td>

                                <td className="px-4 py-3 text-right space-x-2">

                                    {/* Renew/Terminate/Archive */}
                                    <button
                                        onClick={() => handleOpenActionsModal(customer)}
                                        title="Renew / Terminate / Archive"
                                        className="text-gray-500 hover:text-blue-600 p-1 border rounded"
                                    >
                                        <RotateCcw className="w-5 h-5" />
                                    </button>

                                    {/* View */}
                                    <button
                                        onClick={() => handleView(customer.id)}
                                        className="text-gray-500 hover:text-blue-600 p-1 border rounded"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                        </svg>
                                    </button>

                                    {/* Edit */}
                                    <button
                                        onClick={() => handleEdit(customer)}
                                        className="text-gray-500 hover:text-green-600 p-1 border rounded"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                        </svg>
                                    </button>

                                    {/* Delete */}
                                    <button
                                        onClick={() => handleDelete(customer.id, customer.name)}
                                        className="text-gray-500 hover:text-red-600 p-1 border rounded"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                        </svg>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 text-sm text-gray-500">
                Showing {filteredCustomers.length} active policies out of {customers.length} total records.
            </div>

            {(showForm || editingCustomer) && (
                <AddCustomerForm
                    initialData={editingCustomer}
                    onSubmit={handleFormSubmit}
                    onClose={handleCloseModal}
                />
            )}

            {isActionsModalOpen && selectedCustomerForActions && (
                <PolicyActionsModal
                    customer={selectedCustomerForActions}
                    onAction={handlePolicyAction}
                    onClose={() => {
                        setIsActionsModalOpen(false);
                        setSelectedCustomerForActions(null);
                    }}
                />
            )}
        </div>
    );
};

export default CustomerList;
